/* eslint-disable */
"use client";

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { authService } from "@/services/auth.service";
import TermsConsentModal from "@/components/common/TermsConsentModal";

const schema = z.object({
  token: z
    .string()
    .min(1, "쿠폰 코드를 입력해주세요")
    .min(10, "올바른 쿠폰 코드 형식이 아닙니다"),
});

type TokenFields = {
  token: string;
};

interface TokenActivationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (userInfo: any) => void;
  onApproveUser: () => void;
  onTermsComplete?: (userInfo: any) => void;
  initialToken?: string | null;
}

export default function TokenActivationModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  onTermsComplete,
  onApproveUser,
  initialToken 
}: TokenActivationModalProps) {
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
  } = useForm<TokenFields>({ 
    resolver: zodResolver(schema), 
    mode: "onSubmit",
    defaultValues: {
      token: initialToken || ""
    }
  });

  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [showTermsConsent, setShowTermsConsent] = useState(false);
  const [pendingUserInfo, setPendingUserInfo] = useState<any>(null);

  // 초기 토큰이 있으면 입력폼에만 미리 입력
  useEffect(() => {
    if (isOpen && initialToken) {
      setValue('token', initialToken);
    }
  }, [isOpen, initialToken, setValue]);

  const onSubmit = async (data: TokenFields) => {
    try {
      setMessage("");
      // console.log('[TokenActivationModal] Calling authService.activate with token:', data.token);
      const response = await authService.activate(data.token);
      // console.log('[TokenActivationModal] Activate response:', response);
      
      // To-Be 형식 처리
      if (response.code !== "SUCCESS" || !response.data) {
        const errorMessage = response.message || '계정 활성화 중 오류가 발생했습니다.';
        setMessage(errorMessage);
        return;
      }
      
      if (response.data.approvalCode === 'APPROVED') {
        // setMessage("이미 승인된 계정입니다. 로그인 페이지로 이동합니다.");
        // 로그인 페이지로 token 파라미터와 함께 리다이렉트
        onApproveUser();
        return;
      }

      // ActivateResponse를 userInfo 형식으로 변환
      const userInfo = {
        id: response.data.id,
        email: response.data.email,
        name: response.data.name,
        code: response.data.userCode,
        approvalCode: response.data.approvalCode,
        isFirstLogin: response.data.isFirstLogin,
        activationToken: data.token
      };
      
      setIsSuccess(true);
      setMessage("사용이 가능한 쿠폰 번호로 확인 되었습니다.");
      
      // 사용자 정보 저장
      // console.log('[TokenActivationModal] Setting pendingUserInfo:', userInfo);
      setPendingUserInfo(userInfo);
      
      // 2초 후 성공 메시지 숨기고 약관 동의 모달 표시
      setTimeout(() => {
        setIsSuccess(false); // 성공 메시지 숨기기
        setShowTermsConsent(true); // 약관 동의 모달 표시
      }, 2000);
    } catch (error) {
      console.error('Activation error:', error);
      
      const errorMessage = error instanceof Error ? error.message : '계정 활성화 중 오류가 발생했습니다.';
      setMessage(errorMessage);
    }
  };

  const handleClose = () => {
    reset();
    setMessage("");
    setIsSuccess(false);
    setShowTermsConsent(false);
    setPendingUserInfo(null);
    onClose();
  };

  const handleTermsConsentAgree = (consents: any) => {
    // console.log('[TokenActivationModal] Terms consent completed:', consents);
    // 약관 동의 완료 후 onTermsComplete 콜백 호출
    if (onTermsComplete) {
      // pendingUserInfo를 onTermsComplete에 전달할 수 있도록 수정
      onTermsComplete(pendingUserInfo);
    } else {
      // 기존 로직 유지 (fallback)
      if (pendingUserInfo) {
        onSuccess(pendingUserInfo);
      }
    }
    handleClose();
  };

  const handleTermsConsentClose = () => {
    // 약관 동의 모달이 닫힌 경우 전체 모달 닫기
    setShowTermsConsent(false);
    handleClose(); // 전체 모달 닫기
  };

  if (!isOpen) return null;

  return (
    <>
      {/* 쿠폰 모달 - 약관 동의 모달이 열릴 때 숨김 */}
      {!showTermsConsent && (
        <div className="fixed inset-0 flex items-center justify-center z-[9998] p-4 pointer-events-none">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-8 pointer-events-auto">
        <div className="flex justify-between items-start mb-6">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {isSuccess ? "사용 가능 쿠폰번호 확인완료" : "쿠폰 코드 입력"}
            </h2>
            {!isSuccess && (
              <p className="text-gray-600 text-sm">
                쿠폰 코드를 입력하여 회원가입 하시고 모든 서비스를 이용해보세요.
              </p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors ml-4 flex-shrink-0"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {isSuccess ? (
          <div className="text-center">
            <div className="rounded-full h-16 w-16 bg-green-100 flex items-center justify-center mx-auto mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-green-600 font-medium mb-4">{message}</p>
            <p className="text-sm text-gray-500">잠시 후 약관 동의 페이지가 표시됩니다...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label htmlFor="token" className="block text-sm font-medium text-gray-700 mb-2">
                쿠폰 코드
              </label>
              <input
                {...register("token")}
                type="text"
                id="token"
                placeholder="쿠폰 코드를 입력해주세요"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
              {errors.token && (
                <p className="mt-1 text-sm text-red-600">{errors.token.message}</p>
              )}
            </div>

            {message && (
              <div className={`p-3 rounded-lg text-sm ${
                message.includes("성공") 
                  ? "bg-green-50 text-green-700 border border-green-200" 
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}>
                {message}
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? "처리 중..." : "쿠폰등록"}
              </button>
            </div>
          </form>
        )}
          </div>
        </div>
      )}

      {/* 약관 동의 모달 - 별도 렌더링 */}
      <TermsConsentModal
        open={showTermsConsent}
        onClose={handleTermsConsentClose}
        onAgree={handleTermsConsentAgree}
        userCode={pendingUserInfo?.id}
        userEmail={pendingUserInfo?.email}
      />
    </>
  );
}
